/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Speech Search Button - Cross-browser voice search with Whisper AI fallback
 *
 * Supports:
 * - Chrome/Edge: Native Web Speech API (fast, free, instant)
 * - Firefox/Safari/Others: MediaRecorder + Whisper AI (works everywhere)
 */
if (!customElements.get('speech-search-button')) {
  // Feature detection
  const hasNativeSpeech = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  const hasMediaRecorder = 'MediaRecorder' in window && navigator.mediaDevices && navigator.mediaDevices.getUserMedia;
  const hasGetUserMedia = navigator.mediaDevices && navigator.mediaDevices.getUserMedia;
  const hasAudioContext = 'AudioContext' in window || 'webkitAudioContext' in window;

  // WebView detection
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isWebView = (
    // iOS WebView
    (/(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(userAgent)) ||
    // Android WebView
    (/wv/.test(userAgent)) ||
    // Generic WebView indicators
    (/\bwebview\b/i.test(userAgent))
  );

  class SpeechSearchButton extends HTMLElement {
    constructor() {
      super();

      this.recognition = null;
      this.isListening = false;
      this.isProcessing = false;
      this.form = null;
      this.searchInput = null;
      this.predictiveSearch = this.closest('predictive-search');
      this.mediaRecorder = null;
      this.audioChunks = [];

      // Whisper API endpoint - can be configured via data attribute
      this.voiceApiEndpoint = this.getAttribute('data-voice-api') || 'https://kuwait-version6.vercel.app/api/voice-search';

      // Determine which mode to use
      const ua = window.navigator.userAgent.toLowerCase();
      const isChrome = ua.indexOf('chrome') > -1 && !!window.chrome;
      const isEdge = ua.indexOf('edg/') > -1;
      const canUseNative = hasNativeSpeech && (isChrome || isEdge) && !isWebView;

      if (canUseNative) {
        // Chrome/Edge - use native Web Speech API (fast, free)
        this.mode = 'native';
        this.initNative();
      } else if (hasMediaRecorder) {
        // Firefox/Safari/WebView with MediaRecorder - use MediaRecorder + Whisper AI
        this.mode = 'whisper';
        this.initWhisper();
      } else if (isWebView) {
        // WebView - hide voice search, use image search instead
        this.mode = 'hidden';
        this.initHiddenForWebView();
      } else if (hasGetUserMedia && hasAudioContext) {
        // Browsers without MediaRecorder - use Web Audio API + Whisper
        this.mode = 'webview';
        this.initWebView();
      } else {
        // Very old browsers - show unsupported state
        this.mode = 'unsupported';
        this.initUnsupported();
      }

      // Log mode for debugging
      if (isWebView) {
        console.log('[SpeechSearch] WebView detected, voice hidden - use image search');
      }
    }

    /**
     * Initialize native Web Speech API (Chrome/Edge)
     */
    initNative() {
      this.classList.remove('hidden');
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = false;
      this.recognition.lang = document.documentElement.lang || 'en';

      this.form = this.closest('form');
      this.searchInput = this.form.querySelector('.js-search-input');

      if (this.predictiveSearch) {
        this.predictiveSearch.classList.add('speech-search--enabled');
      }

      this.bindNativeEvents();
    }

    /**
     * Bind events for native Web Speech API
     */
    bindNativeEvents() {
      this.recognition.addEventListener(
        'result',
        debounce((evt) => {
          if (evt.results) {
            const term = evt.results[0][0].transcript;
            this.handleTranscript(term);
          }
        }, 300)
      );

      this.recognition.addEventListener('audiostart', () => {
        this.startListening();
      });

      this.recognition.addEventListener('audioend', () => {
        this.stopListening();
      });

      this.recognition.addEventListener('error', (evt) => {
        console.warn('Speech recognition error:', evt.error);
        this.stopListening();
      });

      this.addEventListener('click', this.toggleNative.bind(this));

      this.addEventListener('keydown', (e) => {
        if (e.code === 'Space' || e.code === 'Enter') {
          this.toggleNative(e);
        }
      });
    }

    /**
     * Toggle native speech recognition
     * @param {Event} evt - Event object
     */
    toggleNative(evt) {
      evt.preventDefault();
      if (this.isListening) {
        this.recognition.stop();
      } else {
        this.recognition.start();
      }
    }

    /**
     * Initialize Whisper AI fallback (Firefox/Safari)
     */
    initWhisper() {
      this.classList.remove('hidden');
      this.form = this.closest('form');
      this.searchInput = this.form.querySelector('.js-search-input');

      if (this.predictiveSearch) {
        this.predictiveSearch.classList.add('speech-search--enabled');
      }

      // Add AI indicator badge
      this.setAttribute('data-ai', 'true');

      this.addEventListener('click', this.toggleWhisper.bind(this));

      this.addEventListener('keydown', (e) => {
        if (e.code === 'Space' || e.code === 'Enter') {
          this.toggleWhisper(e);
        }
      });
    }

    /**
     * Toggle Whisper recording
     * @param {Event} evt - Event object
     */
    async toggleWhisper(evt) {
      evt.preventDefault();

      if (this.isProcessing) return;

      if (this.isListening) {
        this.stopWhisperRecording();
      } else {
        await this.startWhisperRecording();
      }
    }

    /**
     * Start recording for Whisper transcription
     */
    async startWhisperRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        // Determine best audio format
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/webm')
            ? 'audio/webm'
            : 'audio/mp4';

        this.mediaRecorder = new MediaRecorder(stream, { mimeType });
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            this.audioChunks.push(e.data);
          }
        };

        this.mediaRecorder.onstop = async () => {
          // Stop all tracks
          stream.getTracks().forEach(track => track.stop());
          await this.sendToWhisper();
        };

        this.mediaRecorder.onerror = (evt) => {
          console.error('MediaRecorder error:', evt.error);
          stream.getTracks().forEach(track => track.stop());
          this.stopListening();
        };

        this.mediaRecorder.start();
        this.startListening();

        // Track voice search usage
        this.trackVoiceSearch('whisper', true);

        // Auto-stop after 10 seconds
        this.recordingTimeout = setTimeout(() => {
          if (this.isListening) {
            this.stopWhisperRecording();
          }
        }, 10000);

      } catch (error) {
        console.error('Microphone access denied:', error);
        const micDeniedMsg = this.getAttribute('data-mic-denied-message')
          || 'Please allow microphone access to use voice search';
        alert(micDeniedMsg);
        this.trackVoiceSearch('whisper', false);
      }
    }

    /**
     * Stop Whisper recording
     */
    stopWhisperRecording() {
      if (this.recordingTimeout) {
        clearTimeout(this.recordingTimeout);
        this.recordingTimeout = null;
      }

      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
      this.stopListening();
    }

    /**
     * Send audio to Whisper API for transcription
     */
    async sendToWhisper() {
      if (this.audioChunks.length === 0) return;

      const audioBlob = new Blob(this.audioChunks, { type: this.mediaRecorder.mimeType });

      // Skip if audio is too short (less than 0.5 seconds)
      if (audioBlob.size < 5000) {
        console.warn('Audio too short for transcription');
        return;
      }

      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', document.documentElement.lang || 'en');

      this.startProcessing();

      try {
        const response = await fetch(this.voiceApiEndpoint, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Transcription failed: ${response.status}`);
        }

        const data = await response.json();

        if (data.text && data.text.trim()) {
          this.handleTranscript(data.text.trim());
        } else if (data.error) {
          console.warn('Whisper API error:', data.error);
        }
      } catch (error) {
        console.error('Whisper API error:', error);
      } finally {
        this.stopProcessing();
      }
    }

    /**
     * Initialize WebView fallback (uses Web Audio API instead of MediaRecorder)
     */
    initWebView() {
      this.classList.remove('hidden');
      this.form = this.closest('form');
      this.searchInput = this.form.querySelector('.js-search-input');

      if (this.predictiveSearch) {
        this.predictiveSearch.classList.add('speech-search--enabled');
      }

      // Add WebView indicator
      this.setAttribute('data-webview', 'true');

      this.addEventListener('click', this.toggleWebView.bind(this));

      this.addEventListener('keydown', (e) => {
        if (e.code === 'Space' || e.code === 'Enter') {
          this.toggleWebView(e);
        }
      });
    }

    /**
     * Toggle WebView recording
     * @param {Event} evt - Event object
     */
    async toggleWebView(evt) {
      evt.preventDefault();

      if (this.isProcessing) return;

      if (this.isListening) {
        this.stopWebViewRecording();
      } else {
        await this.startWebViewRecording();
      }
    }

    /**
     * Start recording using Web Audio API (WebView compatible)
     */
    async startWebViewRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        const AudioContext = window.AudioContext || window.webkitAudioContext;
        this.audioContext = new AudioContext({ sampleRate: 16000 });
        this.audioSource = this.audioContext.createMediaStreamSource(stream);

        // ScriptProcessor for capturing raw audio (deprecated but widely supported)
        this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);
        this.audioBuffers = [];

        this.scriptProcessor.onaudioprocess = (e) => {
          if (this.isListening) {
            const inputData = e.inputBuffer.getChannelData(0);
            // Clone the data as it gets reused
            this.audioBuffers.push(new Float32Array(inputData));
          }
        };

        this.audioSource.connect(this.scriptProcessor);
        this.scriptProcessor.connect(this.audioContext.destination);

        this.mediaStream = stream;
        this.startListening();

        // Track voice search usage
        this.trackVoiceSearch('webview', true);

        // Auto-stop after 10 seconds
        this.recordingTimeout = setTimeout(() => {
          if (this.isListening) {
            this.stopWebViewRecording();
          }
        }, 10000);

      } catch (error) {
        console.error('Microphone access denied:', error);
        const micDeniedMsg = this.getAttribute('data-mic-denied-message')
          || 'Please allow microphone access to use voice search';
        alert(micDeniedMsg);
        this.trackVoiceSearch('webview', false);
      }
    }

    /**
     * Stop WebView recording and process audio
     */
    stopWebViewRecording() {
      if (this.recordingTimeout) {
        clearTimeout(this.recordingTimeout);
        this.recordingTimeout = null;
      }

      this.stopListening();

      // Disconnect audio nodes
      if (this.scriptProcessor) {
        this.scriptProcessor.disconnect();
        this.scriptProcessor = null;
      }
      if (this.audioSource) {
        this.audioSource.disconnect();
        this.audioSource = null;
      }

      // Stop media stream
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
        this.mediaStream = null;
      }

      // Process audio
      if (this.audioBuffers && this.audioBuffers.length > 0) {
        this.processWebViewAudio();
      }

      // Close audio context
      if (this.audioContext) {
        this.audioContext.close();
        this.audioContext = null;
      }
    }

    /**
     * Process recorded audio and send to Whisper API
     */
    async processWebViewAudio() {
      const buffers = this.audioBuffers;
      this.audioBuffers = [];

      // Merge all audio chunks
      const totalLength = buffers.reduce((acc, buf) => acc + buf.length, 0);
      const mergedBuffer = new Float32Array(totalLength);
      let offset = 0;
      for (const buf of buffers) {
        mergedBuffer.set(buf, offset);
        offset += buf.length;
      }

      // Skip if audio is too short
      if (mergedBuffer.length < 8000) { // Less than 0.5 seconds at 16kHz
        console.warn('Audio too short for transcription');
        return;
      }

      // Encode as WAV
      const wavBlob = this.encodeWAV(mergedBuffer, 16000);

      const formData = new FormData();
      formData.append('audio', wavBlob, 'recording.wav');
      formData.append('language', document.documentElement.lang || 'en');

      this.startProcessing();

      try {
        const response = await fetch(this.voiceApiEndpoint, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Transcription failed: ${response.status}`);
        }

        const data = await response.json();

        if (data.text && data.text.trim()) {
          this.handleTranscript(data.text.trim());
        } else if (data.error) {
          console.warn('Whisper API error:', data.error);
        }
      } catch (error) {
        console.error('Whisper API error:', error);
      } finally {
        this.stopProcessing();
      }
    }

    /**
     * Encode Float32Array audio data to WAV format
     * @param {Float32Array} samples - Audio samples
     * @param {number} sampleRate - Sample rate
     * @returns {Blob} - WAV file blob
     */
    encodeWAV(samples, sampleRate) {
      const buffer = new ArrayBuffer(44 + samples.length * 2);
      const view = new DataView(buffer);

      // WAV header
      const writeString = (offset, string) => {
        for (let i = 0; i < string.length; i++) {
          view.setUint8(offset + i, string.charCodeAt(i));
        }
      };

      writeString(0, 'RIFF');
      view.setUint32(4, 36 + samples.length * 2, true);
      writeString(8, 'WAVE');
      writeString(12, 'fmt ');
      view.setUint32(16, 16, true); // Subchunk1Size
      view.setUint16(20, 1, true);  // AudioFormat (PCM)
      view.setUint16(22, 1, true);  // NumChannels
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, sampleRate * 2, true); // ByteRate
      view.setUint16(32, 2, true);  // BlockAlign
      view.setUint16(34, 16, true); // BitsPerSample
      writeString(36, 'data');
      view.setUint32(40, samples.length * 2, true);

      // Audio data
      const volume = 0.8;
      let offset = 44;
      for (let i = 0; i < samples.length; i++) {
        const s = Math.max(-1, Math.min(1, samples[i])) * volume;
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        offset += 2;
      }

      return new Blob([buffer], { type: 'audio/wav' });
    }

    /**
     * Hide voice search in WebView - camera/image search will be used instead
     */
    initHiddenForWebView() {
      // Keep hidden - don't remove 'hidden' class
      this.style.display = 'none';
      this.setAttribute('aria-hidden', 'true');

      // Add webview class to form so CSS can reposition camera button
      this.form = this.closest('form');
      if (this.form) {
        this.form.classList.add('search--webview');
        this.form.classList.remove('search--speech');
      }

      // Also add to predictive search container
      if (this.predictiveSearch) {
        this.predictiveSearch.classList.add('search--webview');
      }
    }

    /**
     * Initialize unsupported browser state
     */
    initUnsupported() {
      this.classList.remove('hidden');
      this.classList.add('speech-search--unsupported');
      this.setAttribute('aria-disabled', 'true');

      // Show tooltip on hover/click
      const unsupportedMsg = this.getAttribute('data-unsupported-message')
        || 'Voice search is not supported in this browser';
      this.setAttribute('title', unsupportedMsg);

      this.addEventListener('click', (e) => {
        e.preventDefault();
        // Could show a toast/notification here
      });

      if (this.predictiveSearch) {
        this.predictiveSearch.classList.add('speech-search--failed');
      }
    }

    /**
     * Handle transcribed text
     * @param {string} text - Transcribed text
     */
    handleTranscript(text) {
      if (!text || !this.searchInput) return;

      this.searchInput.value = text;

      if (this.searchInput.getAttribute('role') === 'combobox') {
        // Predictive search mode - trigger input event
        this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        // Regular search mode - submit form
        this.form.submit();
      }

      // Track successful voice search
      if (this.mode === 'native') {
        this.trackVoiceSearch('native', true);
      }
    }

    /**
     * Start listening state
     */
    startListening() {
      this.isListening = true;
      this.classList.add('search__speech-listening');
    }

    /**
     * Stop listening state
     */
    stopListening() {
      this.isListening = false;
      this.classList.remove('search__speech-listening');
    }

    /**
     * Start processing state (waiting for Whisper)
     */
    startProcessing() {
      this.isProcessing = true;
      this.classList.add('search__speech-processing');
    }

    /**
     * Stop processing state
     */
    stopProcessing() {
      this.isProcessing = false;
      this.classList.remove('search__speech-processing');
    }

    /**
     * Track voice search usage for analytics
     * @param {string} method - 'native' or 'whisper'
     * @param {boolean} success - Whether the operation was successful
     */
    trackVoiceSearch(method, success) {
      // Push to GTM dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'voice_search_used',
        voice_search_method: method,
        voice_search_success: success,
        browser: navigator.userAgent,
        timestamp: new Date().toISOString()
      });

      // Dispatch custom event for theme hooks
      document.dispatchEvent(new CustomEvent('on:search:voice_search_used', {
        bubbles: true,
        detail: { method, success }
      }));

      // Beacon to analytics API
      if (window.searchAnalytics) {
        window.searchAnalytics.track('voice_search_used', {
          voiceMethod: method,
          voiceSuccess: success
        });
      }
    }
  }

  customElements.define('speech-search-button', SpeechSearchButton);
}
